// FIXME circular dependency
// eslint-disable-next-line workspaces/require-dependency
import { actionBarMiddleware } from '@databases/redux/action-bar/middleware';
// FIXME circular dependency
// eslint-disable-next-line workspaces/require-dependency
import { cellSelectionsNoOpMiddleware } from '@databases/redux/cellSelections/noOpMiddleware';
// FIXME circular dependency
// eslint-disable-next-line workspaces/require-dependency
import { presenceMiddleware } from '@databases/redux/presence/middleware';
import { type SerializedError } from '@databases/types';
import { type MiddlewareArray } from '@reduxjs/toolkit';
import { type CurriedGetDefaultMiddleware } from '@reduxjs/toolkit/dist/getDefaultMiddleware';
import { isEqual } from 'lodash-es';
import { batch as batchAdapter } from 'react-redux';
import { type AnyAction, type Middleware, type StateFromReducersMapObject } from 'redux';
import { type ThunkDispatch, type ThunkMiddleware } from 'redux-thunk';

import { deserializeError, getThrottledBatchCallback, sleep } from '../utils';

// FIXME circular dependency
// eslint-disable-next-line import/no-cycle
import contentSlice from './content/slice';
import contextSlice from './context/slice';
// eslint-disable-next-line import/no-cycle
import databasesSlice from './databases/slice';
// eslint-disable-next-line import/no-cycle
import datasourcesSlice from './datasources/slice';
// eslint-disable-next-line import/no-cycle
import jiraSlice from './jira/slice';
// eslint-disable-next-line import/no-cycle
import spacesSlice from './spaces/slice';
// eslint-disable-next-line import/no-cycle
import usersSlice from './users/slice';

export type SimpleStore<S> = {
  getState(): S;
  dispatch: ThunkDispatch<S, void, AnyAction>;
};

export const commonReducers = {
  content: contentSlice.reducer,
  context: contextSlice.reducer,
  databases: databasesSlice.reducer,
  jira: jiraSlice.reducer,
  spaces: spacesSlice.reducer,
  users: usersSlice.reducer,
  datasources: datasourcesSlice.reducer,
};

export type CommonRootState = StateFromReducersMapObject<typeof commonReducers>;

export function getDefaultMiddleware<S>(): (
  getDefaultMiddleware: CurriedGetDefaultMiddleware<S>
) => MiddlewareArray<[ThunkMiddleware<S>, ...Middleware<object, S>[]]> {
  return (getDefaultMiddleware) => {
    return getDefaultMiddleware({
      serializableCheck: false,
    }).concat(
      presenceMiddleware.middleware,
      actionBarMiddleware.middleware,
      cellSelectionsNoOpMiddleware
    );
  };
}

export enum AsyncOperationStatus {
  INITIAL = 'initial',
  PENDING = 'pending',
  SUCCEEDED = 'succeeded',
  ERROR = 'error',
}

export type AsyncOperation<T> =
  | { status: AsyncOperationStatus.INITIAL | AsyncOperationStatus.PENDING }
  | (T extends void
      ? { status: AsyncOperationStatus.SUCCEEDED }
      : { status: AsyncOperationStatus.SUCCEEDED; data: T })
  | { status: AsyncOperationStatus.ERROR; error: SerializedError };

/**
 * If the given load status is an error, throws that error.
 */
export function validateStatus(status: AsyncOperation<any> | undefined): void {
  if (status?.status === AsyncOperationStatus.ERROR) {
    throw deserializeError(status.error);
  }
}

function noThrottle<T>(callback: () => T): Promise<T> {
  return Promise.resolve(callback());
}

/**
 * Loads an item if its load status is undefined or INITIAL. If its load status is PENDING, waits until the status changes.
 * The load operation is delayed using throttledBatch() if throttle is true.
 * @param selectStatus Should returns the current load status of the item
 * @param onStart Should mark the load status as PENDING. Is called if the load status was undefined or INITIAL.
 * @param load Loads the item. Is called if the load status was undefined or INITIAL.
 * @param onSuccess Should mark the load status as SUCCEEDED and put the item in the store. Is called if "load" returned successfully.
 * @param onError Should mark the load status as ERROR and put the error in the store. Is called if "load" raised an exception.
 * @param forceReload If this is true, the item is loaded regardless of its load status.
 */
export async function loadAsync<R>(
  selectStatus: () => AsyncOperation<any> | undefined,
  onStart: () => void,
  load: () => Promise<R>,
  onSuccess: (result: R) => void,
  onError: (error: any) => void,
  {
    forceReload = false,
    throttle = true,
  }: { forceReload?: boolean; throttle?: boolean } = {}
): Promise<void> {
  if (!forceReload) {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const status = await throttledBatch(() => {
        // fetch status
        const status = selectStatus();

        // if we have no status or the status is initial, start loading
        if ([undefined, AsyncOperationStatus.INITIAL].includes(status?.status)) {
          onStart();
        }

        return status;
      });

      if (status?.status === AsyncOperationStatus.PENDING) {
        // if we are pending, busy wait
        await sleep(50);
      } else if (
        [
          undefined,
          AsyncOperationStatus.INITIAL,
          AsyncOperationStatus.SUCCEEDED,
        ].includes(status?.status)
      ) {
        // if we're finished or didn't transition to pending, stop looping
        break;
      } else {
        return;
      }
    }

    // if we already succeeded, don't refetch
    if (selectStatus()?.status === AsyncOperationStatus.SUCCEEDED) {
      return;
    }
  }

  try {
    const result = await load();
    await (throttle ? throttledBatch : noThrottle)(async () => {
      await onSuccess(result);
    });
  } catch (error: any) {
    await (throttle ? throttledBatch : noThrottle)(() => {
      onError(error);
    });
    throw error;
  }
}

/**
 * Groups multiple action dispatches together. Runs the given callback function (which usually dispatches multiple redux actions)
 * and potentially pauses React renders during the execution of the callback (depending on the adapter set by setBatchAdapter()).
 * The frontend should set up react-redux's batch() as an adapter for this.
 */
export function batch(callback: () => void): void {
  isBatching = true;
  try {
    batchAdapter(callback);
  } finally {
    isBatching = false;
    onBatchEndListeners.forEach((listener) => listener());
    onBatchEndListeners = [];
  }
}

let isBatching = false;
let onBatchEndListeners: Array<() => void> = [];

/**
 * Delays the execution of the given callback if a Redux batch operation is currently running.
 * If no batch operation is running, the callback is called immediately. If a batch operation is running, the callback
 * is called synchronously as soon as the batch operation is finished.
 */
export function onBatchEnd(callback: () => void): void {
  if (!isBatching) {
    callback();
  } else {
    onBatchEndListeners.push(callback);
  }
}

let throttledBatchInternal = getThrottledBatchCallback<[() => void]>((callbacks) => {
  let result: any[];
  batch(() => {
    result = callbacks.map(([callback]) => callback());
  });
  return result!;
}, 250);

/**
 * Like batch(), but delays the execution of the callback function by a few milliseconds and then runs the whole
 * pile of callbacks in a single batch operation. This can be used when a lot of items need to be fetched from
 * a remote API to avoid a rerender of the app every single time an item is received.
 */
export async function throttledBatch<R>(callback: () => R): Promise<R> {
  return await throttledBatchInternal(callback);
}

export async function withoutThrottledBatching<T>(cb: () => Promise<T>): Promise<T> {
  const batchFunction = throttledBatchInternal;
  throttledBatchInternal = async (cb) => await cb();
  try {
    return await cb();
  } finally {
    throttledBatchInternal = batchFunction;
  }
}

/**
 * An action with dependencies is a function that calls a chain of selectors and actions, where certain actions
 * need to be redispatched when the result of one selector in the chain changes.
 *
 * A function implementing this interface must exclusively use the "selector" property of its helpers argument to select
 * data from the state, and must dispatch any actions using the "dispatch" property of its helpers argument. A function
 * implementing this interface must be synchronous. Any async actions dispatched must not be awaited. (Error handling
 * of rejected promise is handled by the helper functions.)
 *
 * An example would be resolving an "entry link details" value. The typical way would be to load the destination database
 * (through an action dispatch), then select the destination value (through a selector). If the destination value happens
 * to be another entry link, its target database needs to be loaded as well (through an action dispatch) and the destination
 * value needs to be looked up (through another selector). Whenever the result of a selector in this chain changes, some of
 * its following actions might need to be redispatched (for example, if the destination value becomes an entry link
 * to another database, that other database now needs to be loaded). To avoid a too complex logic, an action with
 * dependencies will simply rerun as soon as the result of any of its selectors changes, expecting any actions dispatched
 * by it to skip already loaded items.
 *
 * This abstraction allows us to define complex selector-action relationships in a way that can be run both in the
 * backend and in the frontend, without losing its ability to be reactive when used in frontend components.
 *
 * In the frontend, useActionWithDependencies() can be called inside a React component. It will wrap all selectors in
 * a useEqualSelector() hook, causing the component to be rerendered and the action to be rerun whenever the result
 * of a selector changes.
 *
 * In the backend, {@see runActionWithDependencies} can be used to run an action once.
 */
export type ActionWithDependencies<S extends SimpleStore<any>, R> = (
  helpers: ActionDependencyHelpers<S>
) => R;

export interface ActionDependencyHelpers<S extends SimpleStore<any>> {
  dispatch: (...args: Parameters<S['dispatch']>) => void;
  selector: <R>(selector: (state: ReturnType<S['getState']>) => R) => R;
}

/**
 * Runs an action with dependencies and returns its result.
 * The action is run in a similar way as it would be run inside a React component: Whenever all dispatched async actions are
 * finished, the whole action is rerun. This function resolves its promise as soon as the action "settles", meaning that two
 * subsequent results don't change the selector results.
 */
export async function runActionWithDependencies<S extends SimpleStore<any>, R>(
  action: ActionWithDependencies<S, R>,
  store: S
): Promise<R> {
  const lastSelectorResults: any[][] = [];
  for (let i = 0; i < 100; i++) {
    const dispatches: Array<Promise<any>> = [];
    const selectorResults: any[] = [];
    const res = action({
      dispatch: (...args: any[]) => {
        // eslint-disable-next-line prefer-spread
        const result = store.dispatch.apply(store, args as any) as any;
        dispatches.push(result);
      },
      selector: (selector) => {
        const result = selector(store.getState());
        selectorResults.push(result);
        return result;
      },
    });

    await Promise.all(dispatches);
    if (
      isEqual(lastSelectorResults[lastSelectorResults.length - 1] ?? [], selectorResults)
    ) {
      return res;
    } else {
      lastSelectorResults.push(selectorResults);
      if (lastSelectorResults.length > 4) {
        lastSelectorResults.shift();
      }
    }
  }

  throw new Error(
    `Infinite loop in action with dependencies. Last selector results were: ${JSON.stringify(
      lastSelectorResults
    )}`
  );
}
